/** Universe and scanner: the funnel, the filters and a virtualised table (U021-U025). */
import {
  decodeFunnelRow,
  decodeScanRow,
  type FunnelRow,
  type ScanRow,
} from "../../contracts/views.ts";
import { PAGE_ROWS_MAX } from "../../contracts/constants.ts";
import { badge, metric, renderFreshnessBar } from "../../app/components.ts";
import { h, setChildren } from "../../app/dom.ts";
import { formatBasisPoints, formatPaise, formatPpm } from "../../app/format.ts";
import {
  renderStateBanner,
  stateOfEnvelope,
  stateOfError,
  type ViewState,
} from "../../app/state.ts";
import { createTable } from "../../app/table.ts";
import type { ViewContext, ViewHandle } from "../../app/view.ts";
import { handleOf, makeFrame } from "../frame.ts";
import {
  NO_FILTERS,
  applyFilters,
  funnelTotal,
  parseWhole,
  sectorsOf,
  type ScanFilters,
} from "./model.ts";

const ROWS_IN_MEMORY_MAX = 2000;

function funnelPanel(rows: readonly FunnelRow[]): HTMLElement {
  return h(
    "section",
    { class: "panel", attrs: { "aria-labelledby": "scanner-funnel" } },
    h("h2", { attrs: { id: "scanner-funnel" } }, "Candidate funnel"),
    h("p", {}, `${String(funnelTotal(rows))} listings accounted for.`),
    h(
      "ul",
      { class: "funnel" },
      ...rows.map((row) =>
        h(
          "li",
          { attrs: { "data-stage": row.stage } },
          badge(row.stage, row.stage === "PASSED" ? "ok" : "info"),
          ` ${String(row.count)}`,
          row.reasons.length === 0
            ? null
            : h(
                "span",
                { class: "reasons" },
                ` — ${row.reasons.map((r) => `${r.reason} ${String(r.count)}`).join(", ")}`,
              ),
        ),
      ),
    ),
  );
}

function select(
  label: string,
  name: string,
  options: readonly string[],
  change: (value: string) => void,
): HTMLElement {
  return h(
    "label",
    {},
    `${label} `,
    h(
      "select",
      {
        attrs: { name },
        on: {
          change: (event) => {
            change((event.target as HTMLSelectElement).value);
          },
        },
      },
      ...options.map((option) => h("option", { attrs: { value: option } }, option)),
    ),
  );
}

function numberBox(label: string, name: string, change: (value: string) => void): HTMLElement {
  return h(
    "label",
    {},
    `${label} `,
    h("input", {
      attrs: { name, type: "text", inputmode: "numeric", autocomplete: "off" },
      on: {
        input: (event) => {
          change((event.target as HTMLInputElement).value);
        },
      },
    }),
  );
}

/** Mount the scanner. */
export function mountScanner(ctx: ViewContext): ViewHandle {
  const frame = makeFrame("scanner", "Universe and scanner");
  frame.banner.append(renderStateBanner({ kind: "loading" }));
  let loaded: ScanRow[] = [];
  let filters: ScanFilters = NO_FILTERS;
  let version = "";
  const table = createTable<ScanRow>({
    label: "Scanner candidates",
    rowKey: (row) => row.listing,
    columns: [
      { key: "listing", header: "Listing", render: (row) => row.listing },
      { key: "sector", header: "Sector", render: (row) => row.sector },
      { key: "side", header: "Side", render: (row) => metric(row.side) },
      {
        key: "stage",
        header: "Stage",
        render: (row) => badge(row.stage, row.stage === "PASSED" ? "ok" : "info"),
      },
      { key: "reason", header: "Reason", render: (row) => metric(row.reason) },
      {
        key: "turnover",
        header: "Turnover",
        render: (row) => metric(row.turnoverPaise === null ? null : formatPaise(row.turnoverPaise)),
      },
      {
        key: "participation",
        header: "Participation",
        render: (row) =>
          metric(row.participationPpm === null ? null : formatPpm(row.participationPpm)),
      },
      {
        key: "gap",
        header: "Gap",
        render: (row) => metric(row.gapBp === null ? null : formatBasisPoints(row.gapBp)),
      },
      { key: "catalyst", header: "Catalyst", render: (row) => metric(row.catalyst) },
    ],
  });
  const summary = h("p", { class: "summary", attrs: { "aria-live": "polite" } });
  const refresh = (): void => {
    const shown = applyFilters(loaded, filters);
    table.setRows(shown);
    summary.textContent = `Showing ${String(shown.length)} of ${String(loaded.length)} loaded rows. Definitions: ${version}.`;
  };
  const filterError = h("p", { class: "filter-error", attrs: { role: "alert" } });
  const setWhole = (text: string, apply: (value: number | null) => ScanFilters): void => {
    const parsed = parseWhole(text);
    filterError.textContent = parsed.ok ? "" : "Enter a whole number or leave the box empty.";
    if (parsed.ok) {
      filters = apply(parsed.value);
      refresh();
    }
  };
  const form = h(
    "form",
    {
      class: "filters",
      attrs: { "aria-label": "Scanner filters" },
      on: {
        submit: (event) => {
          event.preventDefault();
        },
      },
    },
    select("Side", "side", ["ALL", "LONG", "SHORT"], (value) => {
      filters = { ...filters, side: value as ScanFilters["side"] };
      refresh();
    }),
    select(
      "Stage",
      "stage",
      ["ALL", "NOT_EVALUATED", "MISSING_DATA", "REJECTED", "PASSED", "NOT_ALLOCATED"],
      (value) => {
        filters = { ...filters, stage: value as ScanFilters["stage"] };
        refresh();
      },
    ),
    select("Catalyst", "catalyst", ["ANY", "WITH", "WITHOUT"], (value) => {
      filters = { ...filters, catalyst: value as ScanFilters["catalyst"] };
      refresh();
    }),
    numberBox("Minimum turnover (rupees)", "turnover", (text) => {
      setWhole(text, (value) => ({
        ...filters,
        minTurnoverRupees: value === null ? null : BigInt(value),
      }));
    }),
    numberBox("Maximum participation (%)", "participation", (text) => {
      setWhole(text, (value) => ({ ...filters, maxParticipationPercent: value }));
    }),
    numberBox("Maximum gap (basis points)", "gap", (text) => {
      setWhole(text, (value) => ({ ...filters, maxAbsGapBp: value }));
    }),
  );
  const sectorHolder = h("span", { class: "sector-filter" });
  const more = h("button", { attrs: { type: "button", hidden: "" } }, "Load more rows");
  let cursor: string | null = null;
  const bodyState = (state: ViewState): void => {
    setChildren(frame.banner, state.kind === "ready" ? [] : [renderStateBanner(state)]);
  };
  const load = (next: string | null): void => {
    void ctx.client
      .read("scanner", decodeScanRow, {
        accountId: ctx.accountId,
        ...(next === null ? {} : { cursor: next }),
      })
      .then((answer) => {
        if (answer === null || ctx.scope.disposed) {
          return;
        }
        loaded = [...loaded, ...answer.page.rows].slice(0, ROWS_IN_MEMORY_MAX);
        version = loaded[0]?.definitionVersion ?? "none";
        cursor = answer.page.hasMore ? answer.page.nextCursor : null;
        more.hidden = cursor === null;
        bodyState(stateOfEnvelope(answer));
        setChildren(frame.freshness, [
          renderFreshnessBar(answer, { streamSequence: ctx.streamSequence.get() }),
        ]);
        setChildren(sectorHolder, [
          select("Sector", "sector", ["ALL", ...sectorsOf(loaded)], (value) => {
            filters = { ...filters, sector: value };
            refresh();
          }),
        ]);
        refresh();
      })
      .catch((error: unknown) => {
        bodyState(stateOfError(error));
      });
  };
  more.addEventListener("click", () => {
    load(cursor);
  });
  form.append(sectorHolder);
  setChildren(frame.body, [
    h("div", { attrs: { "data-funnel": "" } }),
    form,
    filterError,
    summary,
    table.element,
    more,
  ]);
  const funnelSlot = frame.body.firstElementChild as HTMLElement;
  void ctx.client
    .read("scanner_funnel", decodeFunnelRow, { accountId: ctx.accountId, pageRows: PAGE_ROWS_MAX })
    .then((answer) => {
      if (answer !== null && !ctx.scope.disposed) {
        setChildren(funnelSlot, [
          answer.page.rows.length === 0
            ? renderStateBanner({ kind: "no-observations" })
            : funnelPanel(answer.page.rows),
        ]);
      }
    })
    .catch((error: unknown) => {
      setChildren(funnelSlot, [renderStateBanner(stateOfError(error))]);
    });
  load(null);
  return handleOf(frame, ctx);
}
